import { useEffect, useState } from 'react';
import { APP_STORE_URL, isIPhoneWebSafari } from '../lib/appLinks';

const LEGACY_KEY = 'openruns.iosInstall.dismissedAt';
const KEYS = {
  dashboard: 'openruns.iosInstall.dashboardDismissal.v1',
  'post-join': 'openruns.iosInstall.postJoinDismissal.v1',
};
const DAY = 86400000;
const DISMISSAL_EVENT = 'openruns:ios-install-dismissed';
// No identity data. Failed persistence still respects a dismissal across mounts
// and SPA navigation for this loaded application session, per placement.
const sessionState = { dashboard: null, 'post-join': null };
const emptyState = () => ({ count: 0, dismissedAt: 0 });
const validTime = value => Number.isSafeInteger(value) && value > 0 && value <= Date.now();

function parseState(raw) {
  try {
    const value = JSON.parse(raw);
    const count = Number.isInteger(value?.count) && value.count >= 1 && value.count <= 3 ? value.count : 0;
    return { count, dismissedAt: count && validTime(value?.dismissedAt) ? value.dismissedAt : 0 };
  } catch { return emptyState(); }
}

function writeState(placement, state) {
  sessionState[placement] = state;
  try { window.localStorage.setItem(KEYS[placement], JSON.stringify(state)); }
  catch { console.warn('ios_install_dismissal_storage_unavailable operation=write'); }
}

function readDismissal(placement) {
  let stored = emptyState();
  try {
    const raw = window.localStorage.getItem(KEYS[placement]);
    if (raw !== null) stored = parseState(raw);
    else if (placement === 'dashboard') {
      const legacy = window.localStorage.getItem(LEGACY_KEY);
      const legacyTime = /^\d+$/.test(legacy || '') ? Number(legacy) : 0;
      if (validTime(legacyTime)) {
        stored = { count: 1, dismissedAt: legacyTime };
        // Preserve the legacy record. The new atomic record makes migration
        // idempotent without incrementing count or restarting its timestamp.
        if (!sessionState.dashboard) writeState(placement, stored);
      }
    }
  } catch { console.warn('ios_install_dismissal_storage_unavailable operation=read'); }
  const session = sessionState[placement];
  return session && (session.dismissedAt > stored.dismissedAt ||
    (session.dismissedAt === stored.dismissedAt && session.count > stored.count)) ? session : stored;
}

function suppressionMs(placement, count) {
  if (count === 3) return Infinity;
  return (placement === 'dashboard' ? (count === 2 ? 14 : 3) : 7) * DAY;
}

export default function IOSInstallCTA({ authenticated, placement }) {
  const [dismissal, setDismissal] = useState(() => readDismissal(placement));
  const [now, setNow] = useState(Date.now);
  useEffect(() => {
    const sync = () => { setDismissal(readDismissal(placement)); setNow(Date.now()); };
    const storage = event => {
      if (event.key === KEYS[placement] || event.key === null) {
        sessionState[placement] = null;
        sync();
      } else if (event.key === LEGACY_KEY && placement === 'dashboard') sync();
    };
    window.addEventListener(DISMISSAL_EVENT, sync);
    window.addEventListener('storage', storage);
    sync();
    return () => {
      window.removeEventListener(DISMISSAL_EVENT, sync);
      window.removeEventListener('storage', storage);
    };
  }, [placement]);
  useEffect(() => {
    const remaining = dismissal.dismissedAt + suppressionMs(placement, dismissal.count) - Date.now();
    if (!dismissal.dismissedAt || !Number.isFinite(remaining) || remaining <= 0) return;
    const timer = setTimeout(() => setNow(Date.now()), remaining);
    return () => clearTimeout(timer);
  }, [dismissal, now, placement]);

  if (!authenticated || !isIPhoneWebSafari() || dismissal.count === 3 ||
    (dismissal.dismissedAt && now < dismissal.dismissedAt + suppressionMs(placement, dismissal.count))) return null;
  const dismiss = () => {
    const previous = readDismissal(placement);
    writeState(placement, { count: Math.min(3, previous.count + 1), dismissedAt: Date.now() });
    window.dispatchEvent(new Event(DISMISSAL_EVENT));
  };
  return (
    <aside data-testid={`ios-install-${placement}`} aria-label="Get the Open Runs iOS app"
      className="mb-4 rounded-xl border border-orange-200 bg-orange-50 p-4">
      <h2 className="font-semibold text-zinc-900">Don't miss your next run</h2>
      <p className="mt-1 text-sm text-zinc-700">Get the Open Runs app for push notifications about invitations, game changes and cancellations.</p>
      <div className="mt-3 flex flex-wrap items-center gap-3">
        <a href={APP_STORE_URL} target="_blank" rel="noopener noreferrer"
          className="rounded-lg bg-orange-500 px-4 py-2 text-sm font-semibold text-white hover:bg-orange-600">Get the iOS App</a>
        <button type="button" onClick={dismiss} className="px-3 py-2 text-sm text-zinc-600 hover:text-zinc-900"
          aria-label="Dismiss app installation suggestion">Not now</button>
      </div>
    </aside>
  );
}
